import { Children, type ReactNode, useEffect, useState } from "react";
import { getWorkflowAlerts } from "../../features/alerts/api/alerts-api";
import { loadCameras } from "../../features/cameras/api/cameras-api";
import "./Sidebar.scss";
import "./SidebarButton.scss";
import { UserAccount } from "./UserAccount";
import { SidebarButton } from "./SidebarButton";
import { useCurrentUser } from "../auth/useCurrentUser";

type SidebarProps = {
  children?: ReactNode;
  className?: string;
  activePath?: string;
};

type CameraStatus = {
  online: number;
  total: number;
};

function Icon({ children }: { children: ReactNode }) {
  return (
    <svg
      aria-hidden="true"
      className="icon"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth="2"
    >
      {children}
    </svg>
  );
}

function OwlMark() {
  return (
    <div className="brand-mark">
      <svg aria-hidden="true" viewBox="0 0 64 64" fill="none">
        <path
          d="M15 27V11l12 9M49 27V11L37 20"
          stroke="currentColor"
          strokeWidth="5"
          strokeLinejoin="round"
        />
        <path
          d="M11 32c0-12 9-21 21-21s21 9 21 21v10c0 10-9 16-21 16S11 52 11 42z"
          fill="currentColor"
          fillOpacity=".22"
          stroke="currentColor"
          strokeWidth="4"
        />
        <circle
          cx="24"
          cy="32"
          r="7"
          fill="var(--color-owl-mark-dark)"
          stroke="currentColor"
          strokeWidth="3"
        />
        <circle
          cx="40"
          cy="32"
          r="7"
          fill="var(--color-owl-mark-dark)"
          stroke="currentColor"
          strokeWidth="3"
        />
        <circle cx="24" cy="32" r="2" fill="currentColor" />
        <circle cx="40" cy="32" r="2" fill="currentColor" />
        <path d="m32 35 4 6-4 3-4-3z" fill="currentColor" />
      </svg>
    </div>
  );
}

function LogoutIcon() {
  return (
    <Icon>
      <path d="m16 17 5-5-5-5" />
      <path d="M21 12H9" />
      <path d="M13 5v-1a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2v-1" />
    </Icon>
  );
}

function SettingsIcon() {
  return (
    <Icon>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.12 2.12-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.04 1.56V20h-3v-.08A1.7 1.7 0 0 0 10.66 18.36a1.7 1.7 0 0 0-1.88.34l-.06.06L6.6 16.64l.06-.06A1.7 1.7 0 0 0 7 14.7a1.7 1.7 0 0 0-1.56-1.04H5v-3h.08A1.7 1.7 0 0 0 6.64 9.62a1.7 1.7 0 0 0-.34-1.88L6.24 7.68 8.36 5.56l.06.06a1.7 1.7 0 0 0 1.88.34A1.7 1.7 0 0 0 11.34 4.4V4h3v.08a1.7 1.7 0 0 0 1.04 1.56 1.7 1.7 0 0 0 1.88-.34l.06-.06 2.12 2.12-.06.06a1.7 1.7 0 0 0-.34 1.88 1.7 1.7 0 0 0 1.56 1.04h.08v3h-.08A1.7 1.7 0 0 0 19.4 15Z" />
    </Icon>
  );
}

function ChevronIcon() {
  return (
    <Icon>
      <path d="m7 10 5 5 5-5" />
    </Icon>
  );
}

const navigation = [
  {
    label: "Dashboard",
    href: "/",
    icon: (
      <Icon>
        <>
          <rect width="7" height="9" x="3" y="3" rx="1" />
          <rect width="7" height="5" x="14" y="3" rx="1" />
          <rect width="7" height="9" x="14" y="12" rx="1" />
          <rect width="7" height="5" x="3" y="16" rx="1" />
        </>
      </Icon>
    ),
  },
  {
    label: "Cameras",
    href: "/cameras",
    icon: (
      <Icon>
        <path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3z" />
        <circle cx="12" cy="12" r="3.25" />
      </Icon>
    ),
  },
  {
    label: "Alerts",
    href: "/alerts",
    icon: (
      <Icon>
        <path d="m21.73 18-8-14a2 2 0 0 0-3.46 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
        <path strokeLinecap="round" d="M12 9v4M12 16.5h.01" />
      </Icon>
    ),
  },
  {
    label: "Events",
    href: "/events",
    icon: (
      <Icon>
        <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
      </Icon>
    ),
  },
  {
    label: "Workflow",
    href: "/workflow",
    icon: (
      <Icon>
        <line x1="6" x2="6" y1="3" y2="15" />
        <circle cx="18" cy="6" r="3" />
        <circle cx="6" cy="18" r="3" />
        <path d="M18 9a9 9 0 0 1-9 9" />
      </Icon>
    ),
  },
];

const systemNavigation = [
  {
    label: "Settings",
    href: "/settings",
    icon: <SettingsIcon />,
  },
];

export function Sidebar({
  children,
  className = "",
  activePath,
}: SidebarProps) {
  const sidebarClassName = `sidebar sidebar--collapsible ${className}`.trim();
  const currentUser = useCurrentUser();
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);
  const [cameraStatus, setCameraStatus] = useState<CameraStatus | null>(null);
  const [unacknowledgedAlertCount, setUnacknowledgedAlertCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadStatus = () => {
      void Promise.allSettled([loadCameras(), getWorkflowAlerts()])
        .then(([camerasResult, alertsResult]) => {
          if (cancelled) return;
           setCameraStatus(
             camerasResult.status === "fulfilled"
               ? {
                   online: camerasResult.value.filter((camera) => camera.status === "Live").length,
                   total: camerasResult.value.length,
                 }
               : null,
          );
          setUnacknowledgedAlertCount(
            alertsResult.status === "fulfilled"
              ? alertsResult.value.filter((alert) => !alert.acknowledged).length
              : 0,
          );
        });
    };

    loadStatus();
    const interval = window.setInterval(loadStatus, 30_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);
  const logoutButton = (
    <SidebarButton
      aria-label="Logout"
      className="logout-button"
      role="menuitem"
      onClick={() => {
        setAccountMenuOpen(false);
        void currentUser?.logout?.();
      }}
      type="button"
    >
      <LogoutIcon />
      <span>Logout</span>
    </SidebarButton>
  );

  if (children) {
    const childNodes = Children.toArray(children);

    return (
      <aside className={sidebarClassName}>
        {childNodes[0]}
        <div aria-hidden="true" className="sidebar-separator" />
        {childNodes.slice(1)}
        {logoutButton}
      </aside>
    );
  }

  return (
    <aside className={sidebarClassName}>
      <a className="brand" href="/">
        <OwlMark />
        <div className="brand-copy">
          <p className="brand-name">OWL Vision</p>
        </div>
      </a>
      <nav aria-label="Primary navigation" className="navigation">
        <div className="navigation-group">
          <p className="navigation-title">Monitor</p>
          <div className="navigation-list">
            {navigation.slice(0, 2).map((item) => (
              <a
                className={`navigation-link ${item.href === activePath ? "navigation-link--active" : ""}`}
                href={item.href}
                key={item.label}
                title={item.label}
              >
                {item.icon}
                <span className="navigation-label">{item.label}</span>
              </a>
            ))}
          </div>
        </div>
        <div className="navigation-group">
          <p className="navigation-title">Operations</p>
          <div className="navigation-list">
          {navigation.slice(2).map((item) => (
            <a
              className={`navigation-link ${item.href === activePath ? "navigation-link--active" : ""}`}
              href={item.href}
              key={item.label}
              title={item.label}
            >
              {item.icon}
              <span className="navigation-label">{item.label}</span>
              {item.href === "/alerts" && unacknowledgedAlertCount > 0 && (
                <span className="navigation-count navigation-count--alert">{unacknowledgedAlertCount}</span>
              )}
            </a>
          ))}
          </div>
        </div>
        <div className="navigation-group">
          <p className="navigation-title">System</p>
          <div className="navigation-list">
            {systemNavigation.map((item) => (
              <a
                className={`navigation-link ${activePath?.startsWith(item.href) ? "navigation-link--active" : ""}`}
                href={item.href}
                key={item.label}
                title={item.label}
              >
                {item.icon}
                <span className="navigation-label">{item.label}</span>
              </a>
            ))}
          </div>
        </div>
      </nav>
      <div className="sidebar-footer">
        <a className="sidebar-status" href="/cameras">
          <span
            aria-hidden="true"
             className={`sidebar-status-dot ${cameraStatus === null ? "sidebar-status-dot--unknown" : ""}`}
          />
          <span className="sidebar-status-copy">
             <b>
               {cameraStatus === null
                 ? "System status unknown"
                 : `System status · ${cameraStatus.online}/${cameraStatus.total} online`}
             </b>
          </span>
        </a>
        <div className="sidebar-account">
          {accountMenuOpen && (
            <div className="account-menu" role="menu">
              {logoutButton}
            </div>
          )}
          <button
            aria-expanded={accountMenuOpen}
            aria-haspopup="menu"
            className="operator"
            onClick={() => setAccountMenuOpen((open) => !open)}
            title="Open account menu"
            type="button"
          >
            <UserAccount />
            <ChevronIcon />
          </button>
        </div>
      </div>
    </aside>
  );
}

export default Sidebar;
